import React, { useState, useRef } from "react";
import { Upload, Link2, X, FileText, Video, Music, Globe, Loader2 } from "lucide-react";
import { useContentUpload } from "@/hooks/useContentUpload";
import { useContentPaste } from "@/hooks/useContentPaste";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export interface SourceItem {
  contentId: string;
  title: string;
  type: string;
}

interface SourcesStepProps {
  sources: SourceItem[];
  onAddSource: (source: SourceItem) => void;
  onRemoveSource: (contentId: string) => void;
}

const MAX_SOURCES = 5;

const typeIcon: Record<string, React.ReactNode> = {
  pdf: <FileText className="h-4 w-4" />,
  doc: <FileText className="h-4 w-4" />,
  video: <Video className="h-4 w-4" />,
  youtube: <Video className="h-4 w-4" />,
  audio: <Music className="h-4 w-4" />,
  web: <Globe className="h-4 w-4" />,
  text: <FileText className="h-4 w-4" />,
};

const typeLabel: Record<string, string> = {
  pdf: "PDF",
  doc: "Document",
  video: "Video",
  youtube: "YouTube",
  audio: "Audio",
  web: "Website",
  text: "Text",
};

export const SourcesStep: React.FC<SourcesStepProps> = ({
  sources,
  onAddSource,
  onRemoveSource,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pasteFormOpen, setPasteFormOpen] = useState(false);
  const [pasteLink, setPasteLink] = useState("");
  const [pasteText, setPasteText] = useState("");

  const { uploadContent, uploading } = useContentUpload();
  const { submitContent, isSubmitting } = useContentPaste();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const result = await uploadContent(file);
    if (result) {
      onAddSource({
        contentId: result.id,
        title: file.name.replace(/\.[^/.]+$/, ""),
        type: result.type,
      });
    }
  };

  const handlePasteSubmit = async () => {
    const link = pasteLink.trim() || undefined;
    const textContent = pasteText.trim() || undefined;
    if (!link && !textContent) return;

    const result = await submitContent({ link, textContent });
    if (result.success && result.contentId) {
      const title = link
        ? link.length > 60 ? `${link.slice(0, 60)}…` : link
        : (textContent || "Text").slice(0, 60);
      onAddSource({
        contentId: result.contentId,
        title,
        type: result.contentType || "text",
      });
      setPasteLink("");
      setPasteText("");
      setPasteFormOpen(false);
    }
  };

  const canAdd = sources.length < MAX_SOURCES;
  const nearLimit = sources.length >= 4;
  const isProcessing = uploading || isSubmitting;

  return (
    <div className="flex flex-col gap-6">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.mp3,.wav,.m4a,.mp4,.mov,.avi,.webm"
        onChange={handleFileChange}
        disabled={isProcessing}
      />

      <div className="space-y-2 pb-2">
        <p className="text-sm text-muted-foreground">
          Upload PDFs, audio, video, or paste YouTube URLs and text to build your course from your own materials.
        </p>
        <p className={nearLimit ? "text-xs text-muted-foreground" : "text-xs text-muted-foreground/70"}>
          {sources.length}/{MAX_SOURCES} sources added
        </p>
      </div>

      {sources.length > 0 ? (
        <div className="flex flex-col gap-3">
          {sources.map((source) => (
            <div
              key={source.contentId}
              className="group flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 hover:border-foreground/20 hover:bg-muted/30 transition-all duration-200"
            >
              <span className="text-muted-foreground">
                {typeIcon[source.type] || <FileText className="h-4 w-4" />}
              </span>
              <div className="flex-1 min-w-0">
                <span className="block truncate text-sm">{source.title}</span>
                <span className="text-xs text-muted-foreground capitalize">
                  {typeLabel[source.type] || "File"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRemoveSource(source.contentId)}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all duration-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-6 text-center">
          <p className="text-sm text-muted-foreground">No sources added yet</p>
          <p className="text-xs text-muted-foreground/60">Add files or links to enhance your course</p>
        </div>
      )}

      {canAdd && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
              className="rounded-xl border border-border bg-card px-4 py-4 w-full text-left hover:border-foreground/20 hover:bg-muted/30 transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex gap-3 items-center">
                {uploading ? (
                  <Loader2 className="h-5 w-5 text-muted-foreground shrink-0 animate-spin" />
                ) : (
                  <Upload className="h-5 w-5 text-muted-foreground shrink-0" />
                )}
                <div>
                  <div className="text-sm font-medium">{uploading ? "Uploading…" : "Upload files"}</div>
                  <div className="text-xs text-muted-foreground">PDF, audio, video files</div>
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setPasteFormOpen((v) => !v)}
              disabled={isProcessing}
              className="rounded-xl border border-border bg-card px-4 py-4 w-full text-left hover:border-foreground/20 hover:bg-muted/30 transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex gap-3 items-center">
                <Link2 className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="text-sm font-medium">Paste link / text</div>
                  <div className="text-xs text-muted-foreground">YouTube links, text, websites</div>
                </div>
              </div>
            </button>
          </div>

          {pasteFormOpen && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <input
                type="url"
                value={pasteLink}
                onChange={(e) => setPasteLink(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full rounded-xl border border-border/60 bg-background px-4 py-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                disabled={isSubmitting}
              />

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              <Textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder="Paste your notes, article text, etc."
                rows={3}
                className="rounded-xl border-border/50 px-4 py-3 text-sm resize-none min-h-0"
                disabled={isSubmitting}
              />

              <Button
                type="button"
                onClick={handlePasteSubmit}
                disabled={(!pasteLink.trim() && !pasteText.trim()) || isSubmitting}
                className="w-full rounded-xl"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding…
                  </>
                ) : (
                  "Add source"
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
